import { type ReactElement, type ReactNode, useCallback, useEffect, useRef } from 'react';
import { EFFECT_CLASS } from '@vello/tokens';
import { DialogLayer, UiIcon, useEscape, useLayer, useLayerStack, useModalFocus, type UiIconName } from '@vello/web-theme';

export interface FormDialogProps {
  readonly open: boolean;
  /** Stable id prefix (the title and body ids derive from it). */
  readonly id: string;
  readonly icon: UiIconName;
  /** The icon disc: `danger` for a destructive action, `warning` for a reversible one, `neutral` otherwise. */
  readonly tone?: 'neutral' | 'warning' | 'danger';
  readonly title: string;
  readonly body: string;
  /** An action is in flight: the scrim and Escape do not close. */
  readonly busy: boolean;
  /** The id of the control that takes focus when the dialog opens (default: the first tabbable). */
  readonly initialFocusId?: string;
  readonly onCancel: () => void;
  readonly children: ReactNode;
}

/**
 * The in-app dialog that holds a small FORM (the support cancel, the courier release, the ride's money actions — D378,
 * D382, D438): reason `Select`, labelled note, an error that keeps the dialog open to correct or retry. It is the one
 * shell of those four dialogs, composed from the design system's layer: the `vello-scrim` / `vello-dialog-host` /
 * `vello-dialog` classes, the FX-03 `useLayer` choreography and the shared modal behaviour (`useLayerStack` +
 * `useEscape` + `useModalFocus`: focus moves in, Tab stays inside, Escape cancels only while this is the top layer, focus
 * returns to the opener) instead of a copy of the Tab trap and the focus return in each file (audit admin #12).
 *
 * A pure yes/no goes through the shared `useFeedback().confirm` instead; this shell exists for the dialogs whose refusal
 * must stay inside the dialog next to the fields. Escape and the scrim cancel unless `busy`. The theme draws the focus
 * halo — nothing here adds a second ring.
 */
export function FormDialog({ open, id, icon, tone = 'neutral', title, body, busy, initialFocusId, onCancel, children }: FormDialogProps): ReactElement | null {
  const { mounted, cls } = useLayer(open);
  const ref = useRef<HTMLDivElement>(null);
  const titleId = `${id}-title`;
  const bodyId = `${id}-body`;
  const cancel = useCallback((): void => {
    if (!busy) onCancel();
  }, [busy, onCancel]);
  const isTop = useLayerStack(open);
  useEscape(open, cancel, isTop);
  useModalFocus(ref, open, mounted, isTop);

  // Runs after `useModalFocus` placed focus on the first tabbable: move it to the control the dialog names.
  useEffect(() => {
    if (!open || !mounted || initialFocusId === undefined) return;
    document.getElementById(initialFocusId)?.focus();
  }, [open, mounted, initialFocusId]);

  if (!mounted) return null;

  return (
    <DialogLayer layerClass={cls} onScrimClick={cancel}>
        <div
          ref={ref}
          tabIndex={-1}
          className={`vello-dialog vello-admin-a-dialog ${EFFECT_CLASS.dialog} ${cls}`}
          role="alertdialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={bodyId}
          aria-busy={busy}
          data-vello-layer={open ? 'open' : 'closing'}
        >
          <span className="vello-dialog__icon" aria-hidden="true" data-tone={tone}>
            <UiIcon name={icon} size={22} />
          </span>
          <h2 id={titleId} className="vello-dialog__title">{title}</h2>
          <p id={bodyId} className="vello-dialog__body">{body}</p>
          {children}
        </div>
    </DialogLayer>
  );
}
